import type { CaptchaChallenge } from './api';

interface RecaptchaApi {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { readonly action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
  }
}

export class RecaptchaBrowserUnavailableError extends Error {
  public constructor() {
    super('reCAPTCHA browser API unavailable');
    this.name = 'RecaptchaBrowserUnavailableError';
  }
}

let loading: Promise<void> | null = null;
let loadingSiteKey: string | null = null;

export async function executeRecaptchaChallenge(challenge: CaptchaChallenge): Promise<string> {
  if (!/^[A-Za-z0-9_/]+$/u.test(challenge.action)) {
    throw new RecaptchaBrowserUnavailableError();
  }
  await loadRecaptcha(challenge.siteKey);
  const api = window.grecaptcha;
  if (api === undefined) throw new RecaptchaBrowserUnavailableError();

  return await new Promise<string>((resolve, reject) => {
    api.ready(() => {
      void api
        .execute(challenge.siteKey, { action: challenge.action })
        .then((token) => {
          if (typeof token !== 'string' || token.length === 0 || token.length > 4096) {
            reject(new RecaptchaBrowserUnavailableError());
            return;
          }
          resolve(token);
        })
        .catch(() => reject(new RecaptchaBrowserUnavailableError()));
    });
  });
}

async function loadRecaptcha(siteKey: string): Promise<void> {
  if (window.grecaptcha !== undefined) return;
  if (loading !== null) {
    if (loadingSiteKey !== siteKey) throw new RecaptchaBrowserUnavailableError();
    return await loading;
  }

  loadingSiteKey = siteKey;
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const parameters = new URLSearchParams({ render: siteKey, trustedtypes: 'true' });
    script.src = `https://www.google.com/recaptcha/api.js?${parameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => {
        loading = null;
        loadingSiteKey = null;
        reject(new RecaptchaBrowserUnavailableError());
      },
      { once: true },
    );
    document.head.append(script);
  });

  return await loading;
}
